import { useState, useEffect } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import parse from 'html-react-parser';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { FetchClient } from '@cvent/fetch';
import { withNetworkErrorRetries } from '@cvent/fetch/middleware';

const client = new FetchClient({ networkError: withNetworkErrorRetries() });

const LOG = LoggerFactory.create('useIconFetch');

// Simple in-memory cache to store fetched icons
const iconCache = new Map<string, string>();

const fetch = async (requestURL: string): Promise<string | null> => {
  const response = await client.get(requestURL);
  return response.text();
};

function useIconFetch(resource: RequestInfo): React.ReactNode {
  const [data, setData] = useState(null);

  const requestURL = typeof resource === 'string' ? resource : resource?.url;

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        let result = null;
        let sanitized = null;

        if (iconCache.has(requestURL)) {
          sanitized = iconCache.get(requestURL);
        } else {
          result = await fetch(requestURL);
          sanitized = parse(DOMPurify.sanitize(result.trim()));
          iconCache.set(requestURL, sanitized);
        }

        if (isMounted && sanitized) {
          setData(sanitized);
        }
      } catch (error) {
        if (isMounted) {
          LOG.error(`Failed to fetch icon from ${requestURL}`, error);
        }
      }
    };

    fetchData();

    return () => {
      // Cleanup function to prevent setting state on unmounted component
      isMounted = false;
    };
  }, [requestURL]);

  return data;
}

export default useIconFetch;
